import { useState, useEffect, useCallback } from 'react';
import { Mail, Trash2, Reply, X, Check, Clock, AlertCircle, RefreshCw } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { contactAPI } from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';

const CAT_COLORS = { product: '#7c6dfa', order: '#f59e0b', return: '#ef4444', sales: '#22c55e', payment: '#38bdf8', other: '#9ca3af' };

const statusStyle = (s) => ({
  unread:  { color: 'var(--warning)',  bg: 'rgba(245,158,11,0.12)', icon: <AlertCircle size={12} /> },
  read:    { color: 'var(--text-muted)', bg: 'var(--bg-hover)',       icon: <Check size={12} /> },
  replied: { color: 'var(--success)',  bg: 'rgba(34,197,94,0.12)',   icon: <Reply size={12} /> },
}[s] || {});

export default function AdminContact() {
  const { t } = useSettings();
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  const fetchMessages = useCallback(() => {
    setLoading(true);
    contactAPI.getAll({ status: filter !== 'all' ? filter : undefined })
      .then(r => { setMessages(r.data.contacts); setTotal(r.data.total); setUnread(r.data.unread); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const openMessage = async (msg) => {
    setSelected(msg);
    setReplyText('');
    // Mark as read if unread
    if (msg.status === 'unread') {
      try {
        await contactAPI.updateStatus(msg._id, 'read');
        setMessages(prev => prev.map(m => m._id === msg._id ? { ...m, status: 'read' } : m));
        setUnread(prev => Math.max(0, prev - 1));
      } catch {}
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      const res = await contactAPI.reply(selected._id, replyText);
      toast.success(res.data.message || 'Reply sent!');
      setMessages(prev => prev.map(m => m._id === selected._id ? { ...m, status: 'replied', reply: replyText } : m));
      setSelected(prev => ({ ...prev, status: 'replied', reply: replyText }));
      setReplyText('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error sending reply');
    }
    setReplying(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    setDeleting(id);
    try {
      await contactAPI.delete(id);
      toast.success('Message deleted');
      setMessages(prev => prev.filter(m => m._id !== id));
      if (selected?._id === id) setSelected(null);
    } catch { toast.error('Error deleting'); }
    setDeleting(null);
  };

  return (
    <AdminLayout title={t('admin', 'contact')}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
            {t('admin', 'contact')}
            {unread > 0 && (
              <span style={{ background: 'var(--danger)', color: 'white', borderRadius: 30, padding: '2px 12px', fontSize: 14, fontWeight: 700 }}>
                {unread} new
              </span>
            )}
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>{total} total messages</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {['all', 'unread', 'read', 'replied'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`} style={{ textTransform: 'capitalize' }}>{f}</button>
          ))}
          <button className="btn btn-secondary btn-sm btn-icon" onClick={fetchMessages} title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="admin-contact-layout" style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
        {/* Message list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {loading ? (
            [...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 110, borderRadius: 16 }} />)
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <Mail size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
              <div style={{ fontSize: 16 }}>No messages</div>
            </div>
          ) : messages.map(msg => {
            const ss = statusStyle(msg.status);
            const isActive = selected?._id === msg._id;
            return (
              <div key={msg._id} onClick={() => openMessage(msg)}
                style={{ padding: '16px 18px', background: isActive ? 'var(--accent-dim)' : msg.status === 'unread' ? 'var(--bg-card)' : 'var(--bg-secondary)', border: `1.5px solid ${isActive ? 'var(--accent)' : msg.status === 'unread' ? 'var(--border-light)' : 'var(--border)'}`, borderRadius: 16, cursor: 'pointer', transition: 'var(--transition)', position: 'relative' }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = msg.status === 'unread' ? 'var(--border-light)' : 'var(--border)'; }}>
                {/* Unread dot */}
                {msg.status === 'unread' && (
                  <div style={{ position: 'absolute', top: 16, insetInlineEnd: 16, width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${CAT_COLORS[msg.category] || 'var(--accent)'}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: CAT_COLORS[msg.category] || 'var(--accent)', flexShrink: 0 }}>
                      {msg.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: msg.status === 'unread' ? 800 : 600, fontSize: 14 }}>{msg.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{msg.email}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: ss.bg, color: ss.color, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}>
                      {ss.icon}{msg.status}
                    </span>
                    <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, background: `${CAT_COLORS[msg.category]}18`, color: CAT_COLORS[msg.category], fontWeight: 700, textTransform: 'capitalize' }}>
                      {msg.category}
                    </span>
                  </div>
                </div>
                <div style={{ fontWeight: msg.status === 'unread' ? 700 : 500, fontSize: 14, marginBottom: 4 }}>{msg.subject}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.message}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={11} />{new Date(msg.createdAt).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Message detail + reply */}
        {selected && (
          <div className="admin-contact-detail" style={{ position: 'sticky', top: 80, height: 'fit-content' }}>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>{selected.subject}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
                    {selected.name} · <a href={`mailto:${selected.email}`} style={{ color: 'var(--accent)' }}>{selected.email}</a>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: `${CAT_COLORS[selected.category]}18`, color: CAT_COLORS[selected.category], fontWeight: 700, textTransform: 'capitalize' }}>{selected.category}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={11} />{new Date(selected.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-sm btn-icon" style={{ color: 'var(--danger)', background: 'rgba(244,63,94,0.1)', border: 'none' }}
                    onClick={() => handleDelete(selected._id)} disabled={deleting === selected._id}>
                    <Trash2 size={14} />
                  </button>
                  <button className="btn btn-ghost btn-icon" onClick={() => setSelected(null)}><X size={18} /></button>
                </div>
              </div>

              {/* Message body */}
              <div style={{ padding: '18px 20px', background: 'var(--bg-secondary)', borderRadius: 14, marginBottom: 20, lineHeight: 1.75, fontSize: 15, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                {selected.message}
              </div>

              {/* Previous reply if exists */}
              {selected.reply && (
                <div style={{ padding: '14px 18px', background: 'var(--accent-dim)', border: '1px solid rgba(124,109,250,0.2)', borderRadius: 14, marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Reply size={12} /> Previous Reply · {selected.repliedAt ? new Date(selected.repliedAt).toLocaleDateString() : ''}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{selected.reply}</div>
                </div>
              )}

              {/* Reply form */}
              <form onSubmit={handleReply}>
                <div className="form-group">
                  <label className="form-label">
                    {selected.reply ? 'Send Another Reply' : `Reply to ${selected.name}`}
                  </label>
                  <textarea className="form-input" rows={5} value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." required />
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setSelected(null)}>Close</button>
                  <button type="submit" className="btn btn-primary" disabled={replying}>
                    <Reply size={16} />{replying ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
